import type { SymbolMappingService } from "./symbol-mapping-service";
import {
  parseOptionSymbolIdentity,
  toOccSymbol,
} from "@/lib/symbols/symbol-identity";

export interface SymbolVariants {
  trimmed: string;
  mappingPosition: string | null;
  compactSymbol: string | null;
  canonicalStreamer: string | null;
  canonicalPosition: string | null;
}

export function deriveSymbolVariants(
  rawSymbol: string,
  mappingService: SymbolMappingService,
  fallbackPosition?: string | null,
): SymbolVariants {
  const trimmed = (rawSymbol || "").trim();
  const upper = trimmed.toUpperCase();
  const fallback = fallbackPosition ? fallbackPosition.trim() : null;

  const mappingPosition =
    mappingService.getPositionSymbol(trimmed) ||
    mappingService.getPositionSymbol(upper) ||
    fallback ||
    null;

  const parsed =
    parseOptionSymbolIdentity(trimmed) ||
    (mappingPosition ? parseOptionSymbolIdentity(mappingPosition) : null);

  if (!parsed) {
    return {
      trimmed,
      mappingPosition,
      compactSymbol: null,
      canonicalStreamer: trimmed.startsWith(".") ? trimmed : null,
      canonicalPosition: mappingPosition,
    };
  }

  const compactSymbol = parsed.compact;
  const canonicalStreamer = `.${compactSymbol}`;
  const canonicalPosition = toOccSymbol(compactSymbol);

  return {
    trimmed,
    mappingPosition: mappingPosition ?? canonicalPosition,
    compactSymbol,
    canonicalStreamer,
    canonicalPosition,
  };
}

export interface BuildAliasKeysOptions {
  hasKey?: (key: string) => boolean;
}

export function buildAliasKeys(
  candidates: Array<string | null | undefined>,
  options?: BuildAliasKeysOptions,
): string[] {
  const keys = new Set<string>();
  const hasKey = options?.hasKey;

  const register = (value: string | null | undefined) => {
    if (!value) {
      return;
    }
    const trimmed = value.trim();
    if (!trimmed) {
      return;
    }

    const variants = new Set<string>([value, trimmed, trimmed.toUpperCase()]);

    const collapsed = trimmed.replace(/\s+/g, "");
    if (collapsed) {
      variants.add(collapsed);
      variants.add(collapsed.toUpperCase());
    }

    if (trimmed.startsWith(".")) {
      const withoutDot = trimmed.slice(1);
      if (withoutDot) {
        variants.add(withoutDot);
        variants.add(withoutDot.toUpperCase());
      }
    }

    variants.forEach((variant) => {
      const normalized = variant.trim();
      if (!normalized) {
        return;
      }
      keys.add(normalized);
      keys.add(normalized.toUpperCase());
      if (hasKey) {
        const lower = normalized.toLowerCase();
        if (hasKey(lower)) {
          keys.add(lower);
        }
      }
    });
  };

  candidates.forEach(register);

  return Array.from(keys).filter((key) => key.length > 0);
}

export function removeLegacyAliases<T>(
  cache: Map<string, T>,
  target: T,
  preservedKeys: Set<string>,
): void {
  for (const [key, value] of cache.entries()) {
    if (value === target && !preservedKeys.has(key)) {
      cache.delete(key);
    }
  }
}
